import { useCallback, useState } from "react";
import { Button, Link } from "@mui/material";
import { toExternalHref } from "utils/url";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import PlanPickerDrawer from "components/platform/accounts/PlanPickerDrawer";
import { GET_COMPANY_PLANS } from "graphql/platform";
import { UPDATE_COMPANY_PLANS } from "graphql/company";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { FormikAddAccount } from "hooks/useAccountData";
import { AccountDetailsDrawerType } from "enums/AccountDetailsDrawerType";
import EditAccountDrawer from "./EditAccountDrawer";
import { Drawer } from "@outpave/ds";
import Summary from "components/common/Summary";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { fullName } from "utils/fullName";

type PlanSelection = {
  appPlanIds: string[];
  addOnPlanIds: string[];
};

interface AccountDetailsDrawerProps {
  open: boolean;
  formik: FormikProps<FormikAddAccount>;
  drawerType: string;
  handleCloseDrawer: (refresh?: boolean) => void;
  onBack?: () => void;
  /** Account create/update in flight. Gates the overlay and every close path. */
  savingAccount?: boolean;
  accountId?: string;
}

const AccountDetailsDrawer = (props: AccountDetailsDrawerProps) => {
  const {
    open,
    formik,
    drawerType,
    handleCloseDrawer,
    onBack,
    savingAccount = false,
    accountId,
  } = props;
  const { t } = useTranslation();

  const [openEditAccountDrawer, setOpenEditAccountDrawer] = useState(false);
  const [openPlanPicker, setOpenPlanPicker] = useState(false);

  const canManagePlans = useCheckUserRole(Role.PlatformOwner);
  const isEditExisting =
    drawerType === AccountDetailsDrawerType.EditExistingAccount;
  const isNewAccount = drawerType === AccountDetailsDrawerType.NewAccount;

  const { data: companyPlansData, refetch: refetchCompanyPlans } = useQuery(
    GET_COMPANY_PLANS,
    {
      variables: { companyId: accountId },
      skip: !open || !isEditExisting || !accountId || !canManagePlans,
      fetchPolicy: "network-only",
    },
  );

  const [updateCompanyPlans, { loading: savingPlans }] = useMutation(
    UPDATE_COMPANY_PLANS,
    {
      onCompleted: () => {
        toast.success(t("planUpdated"));
        refetchCompanyPlans();
      },
      onError: (err) => {
        toast.error(err?.message || t("failedToUpdatePlans"));
      },
    },
  );

  const handlePlanSave = (next: PlanSelection) => {
    if (!accountId) return;
    updateCompanyPlans({
      variables: {
        companyId: accountId,
        input: { appPlanIds: next.appPlanIds, addOnPlanIds: next.addOnPlanIds },
      },
    });
  };

  const websiteUrl = formik.values.account.websiteURL;
  const websiteValue = websiteUrl ? (
    <Link
      href={toExternalHref(websiteUrl)}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
    >
      {websiteUrl}
    </Link>
  ) : (
    ""
  );

  const accountSummaryItems =
    drawerType === AccountDetailsDrawerType.NewAccount
      ? [
          {
            title: t("platform.accountName"),
            value: `${formik.values.account.businessLegalName}`,
          },
        ]
      : [
          {
            title: "Legal Name",
            value: `${formik.values.account.businessLegalName}`,
          },
          {
            title: "Doing Business As (DBA)",
            value: `${formik.values.account.businessDBA}`,
          },
          {
            title: "Business Card Name",
            value: `${formik.values.account.businessCardName}`,
          },
          {
            title: "Description",
            value: `${formik.values.account.description}`,
            fullWidth: true,
          },
          {
            title: "Email",
            value: `${formik.values.account.businessEmail}`,
            fullWidth: true,
          },
          {
            title: "Phone",
            value: `${formik.values.account.phoneNumber}`,
          },
          {
            title: "Website",
            value: websiteValue,
            fullWidth: true,
          },
          {
            title: "Card Design",
            value: `${formik.values.account.cardDesign?.name}`,
          },
        ];

  const summaryItems = [
    {
      title: t("Name"),
      value: fullName(formik.values.user),
      shouldShow: true,
    },
    {
      title: t("Email"),
      value: formik.values.user.email,
      shouldShow: true,
      fullWidth: true,
    },
    {
      title: t("Phone"),
      value: formik.values.user.phone,
      shouldShow: true,
    },
    {
      title: t("Address"),
      value: formik.values.user.address?.addressName,
      shouldShow: true,
    },
  ].filter((field) => field.shouldShow);

  // Gates the overlay AND every dismissal path, so the two can't drift.
  // `savingPlans` is the plan mutation this drawer owns; `savingAccount` is the
  // create/update the parent runs. The parent's own LoadingWithoutModal sits at
  // zIndex.drawer + 1, below this drawer's modal, so it never covered it.
  const busy = savingAccount || savingPlans;

  const handleCloseAccountDetailsDrawer = useCallback(() => {
    if (busy) return;
    handleCloseDrawer(false);
  }, [busy, handleCloseDrawer]);

  const handleEditAccountDrawerClose = useCallback(
    (refresh?: boolean) => {
      setOpenEditAccountDrawer(false);
      if (!refresh) {
        formik.resetForm();
      }
    },
    [formik],
  );

  const handleSaveAccount = () => {
    formik.submitForm();
  };

  return (
    <>
      <Drawer
        loading={busy}
        title={
          drawerType === AccountDetailsDrawerType.NewAccount
            ? t("platform.reviewAccount")
            : t("platform.accountDetails")
        }
        open={open}
        onClose={handleCloseAccountDetailsDrawer}
        footer={
          isNewAccount ? (
            <>
              <Button
                variant="primary-outlined"
                disabled={busy}
                onClick={onBack ?? handleCloseAccountDetailsDrawer}
              >
                {t("Back")}
              </Button>
              <Button
                variant="primary-filled"
                disabled={busy}
                onClick={handleSaveAccount}
              >
                {t("Save")}
              </Button>
            </>
          ) : (
            <Button
              variant="default-outlined"
              disabled={busy}
              onClick={handleCloseAccountDetailsDrawer}
            >
              {t("Exit")}
            </Button>
          )
        }
      >
        <Stack>
          <Summary items={accountSummaryItems} disableContainer>
            {!isNewAccount && (
              <Inline gap="var(--space-3)" justify="center">
                {isEditExisting && canManagePlans && (
                  <Button
                    variant="primary-outlined"
                    size="small"
                    disabled={busy}
                    onClick={() => setOpenPlanPicker(true)}
                  >
                    {t("Manage Plans")}
                  </Button>
                )}
                <Button
                  variant="primary-filled"
                  size="small"
                  disabled={busy}
                  onClick={() => setOpenEditAccountDrawer(true)}
                >
                  {t("Edit")}
                </Button>
              </Inline>
            )}
          </Summary>

          {isNewAccount && <Summary items={summaryItems} disableContainer />}
        </Stack>
      </Drawer>

      {openEditAccountDrawer && (
        <EditAccountDrawer
          open={openEditAccountDrawer}
          handleClose={handleEditAccountDrawerClose}
          drawerType={AccountDetailsDrawerType.EditExistingAccount}
          formik={formik}
          companyId={accountId}
        />
      )}
      {isEditExisting && canManagePlans && (
        <PlanPickerDrawer
          open={openPlanPicker}
          onClose={() => setOpenPlanPicker(false)}
          appPlanIds={companyPlansData?.companyPlans?.appPlanIds || []}
          addOnPlanIds={companyPlansData?.companyPlans?.addOnPlanIds || []}
          onSave={handlePlanSave}
          companyName={formik.values.account.businessLegalName}
        />
      )}
    </>
  );
};

export default AccountDetailsDrawer;
